import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { duration, formatDate } from '../lib/format';
import type { DocVersionAnalytics } from '../lib/types';

export default function DocAnalyticsPage() {
  const { projectId = '', docId = '' } = useParams();
  const q = useQuery({
    queryKey: ['docAnalytics', projectId, docId],
    queryFn: () => api.docAnalytics(projectId, docId),
    refetchInterval: 60_000,
  });
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
  });
  const [selected, setSelected] = useState<number | null>(null);
  const thumbs = useQueryThumbs(projectId, docId, q.data?.doc.currentVersion ?? 0);

  if (q.isLoading) return <p className="text-soft">Loading…</p>;
  if (!q.data) return <p className="text-soft">Document not found.</p>;
  const { doc, analytics } = q.data;
  const withData = analytics.filter((a) => a.status === 'ready');
  const active =
    withData.find((a) => a.version === (selected ?? doc.currentVersion)) ??
    withData[withData.length - 1];

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <Link to="/" className="rw-heading text-xs text-soft hover:text-white">
          Projects
        </Link>
        <span className="text-mid">/</span>
        <Link
          to={`/p/${projectId}`}
          className="rw-heading text-xs text-soft hover:text-white"
        >
          {project.data?.project.name ?? '…'}
        </Link>
        <span className="text-mid">/</span>
        <h1 className="rw-heading text-sm text-white">{doc.title}</h1>
      </div>

      {withData.length === 0 && (
        <p className="border border-dashed border-white/20 p-10 text-center text-sm text-soft">
          No processed versions yet.
        </p>
      )}

      {withData.length > 0 && active && (
        <>
          <div className="mb-5 flex flex-wrap gap-2">
            {withData.map((a) => (
              <button
                key={a.version}
                onClick={() => setSelected(a.version)}
                title={`Uploaded ${formatDate(a.uploadedAt)} · ${a.visits} visits`}
                className={`border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${
                  active.version === a.version
                    ? 'border-accent bg-accent text-white'
                    : 'border-white/25 text-soft hover:border-white/50 hover:text-white'
                }`}
              >
                v{a.version}
                {a.version === doc.currentVersion && ' · live'}
              </button>
            ))}
          </div>

          <div className="mb-8 grid grid-cols-2 gap-3 sm:max-w-2xl sm:grid-cols-4">
            <Stat label="Visits" value={String(active.visits)} />
            <Stat
              label="Avg completion"
              value={active.avgCompletionPct !== null ? `${active.avgCompletionPct}%` : '—'}
            />
            <Stat label="Total time" value={duration(active.totalSeconds)} />
            <Stat label="Pages" value={String(active.pageCount ?? '—')} />
          </div>

          <Chart
            title="Average time per page"
            analytics={active}
            value={(p) => p.avgSeconds}
            fmt={(v) => `${v}s avg`}
            thumbs={active.version === doc.currentVersion ? thumbs : undefined}
          />
          <Chart
            title="Dropoff — share of visits reaching each page"
            analytics={active}
            value={(p) => p.reachedPct}
            fmt={(v) => `${v}% of visits`}
            pct
            thumbs={active.version === doc.currentVersion ? thumbs : undefined}
          />
        </>
      )}
    </div>
  );
}

function useQueryThumbs(projectId: string, docId: string, version: number) {
  const q = useQuery({
    queryKey: ['thumbs', projectId, docId, version],
    queryFn: () => api.docThumbs(projectId, docId, version),
    enabled: version > 0,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  return q.data?.thumbs;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rw-card p-4">
      <div className="font-heading text-2xl font-semibold text-ink">{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wider text-mid">{label}</div>
    </div>
  );
}

function Chart({
  title,
  analytics,
  value,
  fmt,
  pct = false,
  thumbs,
}: {
  title: string;
  analytics: DocVersionAnalytics;
  value: (p: DocVersionAnalytics['pages'][number]) => number;
  fmt: (v: number) => string;
  pct?: boolean;
  thumbs?: string[];
}) {
  const [hover, setHover] = useState<number | null>(null);
  const max = pct ? 100 : Math.max(1, ...analytics.pages.map(value));
  return (
    <div className="relative mb-8">
      <h2 className="rw-heading mb-2 text-xs text-white">{title}</h2>
      {hover !== null && (
        <div className="pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2 border border-line bg-white p-1.5 shadow-lg">
          {thumbs?.[hover - 1] && (
            <img src={thumbs[hover - 1]} alt="" className="mb-1 w-28 border border-line/50" />
          )}
          <div className="text-center text-[10px] font-medium text-charcoal">
            Page {hover} · {fmt(value(analytics.pages[hover - 1]))}
          </div>
        </div>
      )}
      <div className="rw-card flex items-end gap-[3px] p-4" style={{ height: 150 }}>
        {analytics.pages.map((p) => {
          const v = value(p);
          return (
            <div
              key={p.page}
              onMouseEnter={() => setHover(p.page)}
              onMouseLeave={() => setHover((h) => (h === p.page ? null : h))}
              className="min-w-[6px] flex-1 cursor-pointer"
              style={{
                height: `${Math.max(3, (v / max) * 100)}%`,
                backgroundColor: v > 0 ? '#8b1a10' : '#e8e8e5',
                opacity: v > 0 ? 0.45 + 0.55 * (v / max) : 1,
              }}
            />
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-soft">
        <span>Page 1</span>
        <span>Page {analytics.pageCount}</span>
      </div>
    </div>
  );
}
